import { useEffect, useRef } from "react";

/**
 * Debounced autosave, like the mock's 400ms `saveSoon`. The latest call wins and runs once the participant pauses.
 * Pending work is flushed on unmount, so leaving a screen mid-sentence still saves.
 */
export function useDebouncedCallback<A extends unknown[]>(fn: (...args: A) => void, delay = 500) {
  const fnRef = useRef(fn);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pending = useRef<A | null>(null);
  fnRef.current = fn;

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
      if (pending.current) fnRef.current(...pending.current);
    },
    [],
  );

  return (...args: A) => {
    pending.current = args;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      pending.current = null;
      fnRef.current(...args);
    }, delay);
  };
}
